import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const StrategyPage = () => {
  const heroRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const acquisitionRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const valueAddRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const operationsRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const riskRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const horizonRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const ctaRef = useScrollAnimation<HTMLDivElement>('fade-in-up');

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center bg-[#1F2A44] mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)' }}></div>
        </div>
        <div ref={heroRef} className="relative max-w-[1440px] mx-auto px-20 w-full text-center">
          <div className="w-16 h-0.5 bg-[#B89C4B] mx-auto mb-6"></div>
          <h1
            className="text-[56px] text-white leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Investment Strategy
          </h1>
          <p
            className="text-[18px] text-white/85 max-w-[640px] mx-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Disciplined value-add approach focused on multifamily properties in Midwest markets with clear paths to operational improvement and NOI growth
          </p>
        </div>
      </section>

      {/* Acquisition Criteria Section */}
      <section className="bg-white py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <h2
            className="text-[44px] text-[#1F2A44] mb-12"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            What We Look For
          </h2>

          <div ref={acquisitionRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              {[
                {
                  title: 'Property Size',
                  description: '10–50 unit multifamily properties that allow for hands-on management and operational control',
                },
                {
                  title: 'Market Selection',
                  description: 'Midwest markets with stable employment, population growth, and strong rental demand fundamentals',
                },
                {
                  title: 'Value-Add Potential',
                  description: 'Properties with clear opportunities for interior upgrades, operational improvements, or rent optimization',
                },
                {
                  title: 'Conservative Leverage',
                  description: 'Disciplined use of debt with focus on capital preservation and downside protection',
                },
                {
                  title: 'Exit Flexibility',
                  description: 'Multiple exit scenarios including refinance, sale to institutional buyer, or long-term hold',
                },
              ].map((criteria, index) => (
                <div 
                  key={index} 
                  className="flex items-start"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className="flex-shrink-0 w-6 h-6 mt-1 mr-4">
                    <i className="ri-checkbox-circle-fill text-2xl text-[#B89C4B]"></i>
                  </div>
                  <div>
                    <h3
                      className="text-[18px] text-[#1F2A44] font-semibold mb-2"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {criteria.title}
                    </h3>
                    <p
                      className="text-[15px] text-[#2E2E2E] leading-relaxed"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {criteria.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/a98bc0645312aa760bd35d863308175a.jpg"
                alt="Investment Analysis"
                className="w-full h-[600px] object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value-Add Strategy Section */}
      <section className="bg-[#F5F5F3] py-36">
        <div className="max-w-[1440px] mx-auto px-20">
          <h2
            className="text-[46px] text-[#1F2A44] text-center mb-14"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Creating Value Through Strategic Improvements
          </h2>

          <div className="relative">
            <div className="absolute top-20 left-0 right-0 h-0.5 bg-[#B89C4B] hidden lg:block" style={{ backgroundImage: 'repeating-linear-gradient(to right, #B89C4B 0, #B89C4B 10px, transparent 10px, transparent 20px)' }}></div>

            <div ref={valueAddRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  phase: '01',
                  title: 'Acquisition',
                  description: 'Conservative underwriting with multiple stress scenarios. Focus on properties with clear value-add potential and strong market fundamentals.',
                },
                {
                  phase: '02',
                  title: 'Renovation',
                  description: 'Strategic capital improvements to units and common areas. Scope determined by market positioning and return on investment analysis.',
                },
                {
                  phase: '03',
                  title: 'Operations',
                  description: 'Implementation of structured property management systems. Focus on tenant retention, expense control, and service quality.',
                },
                {
                  phase: '04',
                  title: 'Optimization',
                  description: 'Ongoing asset management to drive NOI growth. Position property for refinance, sale, or long-term hold based on market conditions.',
                },
              ].map((phase, index) => (
                <div 
                  key={index} 
                  className="relative"
                  style={{
                    animationDelay: `${index * 0.15}s`,
                  }}
                >
                  <div className="flex flex-col items-center mb-6">
                    <div className="w-20 h-20 rounded-full bg-white border-4 border-[#B89C4B] flex items-center justify-center shadow-md relative z-10">
                      <span
                        className="text-[24px] text-[#1F2A44] font-bold"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}
                      >
                        {phase.phase}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white p-8 rounded-lg shadow-md">
                    <h3
                      className="text-[19px] text-[#1F2A44] font-semibold mb-4 text-center"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {phase.title}
                    </h3>
                    <p
                      className="text-[15px] text-[#2E2E2E] leading-relaxed text-center"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {phase.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Asset Management Section */}
      <section className="bg-[#1F2A44] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={operationsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2
                className="text-[42px] text-white leading-tight mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              >
                Operational Excellence
              </h2>
              <p
                className="text-[16px] text-white/85 leading-relaxed mb-8"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our asset management approach focuses on implementing structured systems that drive operational efficiency, tenant satisfaction, and NOI growth. We believe that disciplined property management is the foundation of long-term value creation.
              </p>
              <p
                className="text-[16px] text-white/85 leading-relaxed"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Every property receives regular oversight, detailed financial reporting, and proactive maintenance to ensure performance meets or exceeds underwriting projections.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: 'ri-user-heart-line',
                  title: 'Tenant Relations',
                  description: 'Responsive service and clear communication to improve retention and reduce turnover costs',
                },
                {
                  icon: 'ri-tools-line',
                  title: 'Preventive Maintenance',
                  description: 'Proactive systems to reduce emergency repairs and extend asset life',
                },
                {
                  icon: 'ri-file-list-3-line',
                  title: 'Financial Controls',
                  description: 'Detailed budgeting, expense tracking, and variance analysis',
                },
                {
                  icon: 'ri-line-chart-line',
                  title: 'Revenue Optimization',
                  description: 'Strategic rent positioning based on market analysis and unit quality',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Risk Management',
                  description: 'Comprehensive insurance, legal compliance, and safety protocols',
                },
                {
                  icon: 'ri-bar-chart-box-line',
                  title: 'Performance Monitoring',
                  description: 'Regular KPI tracking and reporting to identify opportunities and address issues',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white/8 border border-white/15 p-6 rounded-lg flex items-start"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className="flex-shrink-0 w-10 h-10 mr-4 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                    <i className={`${item.icon} text-lg text-[#B89C4B]`}></i>
                  </div>
                  <div>
                    <h3
                      className="text-[17px] text-white font-semibold mb-2"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-[14px] text-white/80 leading-relaxed"
                      style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 300 }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Risk Management Section */}
      <section className="bg-white py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="max-w-[960px] mx-auto">
            <h2
              className="text-[40px] text-[#1F2A44] text-center mb-12"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Conservative Underwriting
            </h2>

            <div ref={riskRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-calculator-line',
                  title: 'Stress Testing',
                  description: 'Every acquisition is modeled through multiple downside scenarios including vacancy increases, expense overruns, and delayed lease-up.',
                },
                {
                  icon: 'ri-funds-line',
                  title: 'Capital Reserves',
                  description: 'Adequate reserves for unexpected repairs, market downturns, and renovation contingencies to protect investor capital.',
                },
                {
                  icon: 'ri-shield-line',
                  title: 'Conservative Leverage',
                  description: 'Disciplined use of debt with focus on maintaining flexibility and avoiding over-leverage that increases risk.',
                },
                {
                  icon: 'ri-pie-chart-line',
                  title: 'Market Analysis',
                  description: 'Thorough evaluation of local employment, population trends, new supply, and competitive positioning.',
                },
                {
                  icon: 'ri-time-line',
                  title: 'Exit Planning',
                  description: 'Multiple exit strategies identified at acquisition including refinance, sale, or long-term hold options.',
                },
                {
                  icon: 'ri-team-line',
                  title: 'Due Diligence',
                  description: 'Comprehensive property inspections, financial audits, and legal review before closing any transaction.',
                },
              ].map((principle, index) => (
                <div 
                  key={index} 
                  className="text-center"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className="w-16 h-16 mx-auto mb-5 border-2 border-[#B89C4B] rounded-full flex items-center justify-center">
                    <i className={`${principle.icon} text-2xl text-[#B89C4B]`}></i>
                  </div>
                  <h3
                    className="text-[18px] text-[#1F2A44] font-semibold mb-3"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {principle.title}
                  </h3>
                  <p
                    className="text-[15px] text-[#2E2E2E] leading-relaxed"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {principle.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Investment Horizon Section */}
      <section className="bg-[#F5F5F3] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={horizonRef} className="max-w-[800px] mx-auto text-center">
            <h2
              className="text-[42px] text-[#1F2A44] leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Long-Term Value Creation
            </h2>
            <p
              className="text-[17px] text-[#2E2E2E] leading-relaxed mb-8"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              We typically target a 5–7 year hold period to allow sufficient time for value-add improvements, operational stabilization, and market appreciation. This patient capital approach reduces pressure to exit during unfavorable market conditions.
            </p>
            <p
              className="text-[17px] text-[#2E2E2E] leading-relaxed"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Our focus is on sustainable value creation through operational improvements rather than financial engineering or market timing. This approach provides more predictable returns and better downside protection for our investors.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24">
        <div ref={ctaRef} className="max-w-[1440px] mx-auto px-20 text-center">
          <h2
            className="text-[38px] text-[#1F2A44] mb-5"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Explore Investment Opportunities
          </h2>
          <p
            className="text-[16px] text-[#2E2E2E] mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Learn more about our current portfolio and upcoming acquisitions
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/portfolio"
              className="px-10 py-3.5 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              View Portfolio
            </Link>
            <Link
              to="/investors"
              className="px-10 py-3.5 border-2 border-[#1F2A44] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Investor Relations
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StrategyPage;